import React from "react";
import styled from "styled-components";

import { brand_orange_deep } from "../constants/styles";
import { SEARCH_PHASE } from "../hooks/useDebouncedSearch";

// The line under a debounced search box that tells the user what the search is
// doing. Without it, a deliberate 900ms wait and a deliberate 3-character
// threshold are indistinguishable from a broken input.
//
// Shared by /webinars and /video-pembelajaran so both report identically.
//
// `aria-live="polite"` on a container that is always in the DOM: a live region
// that gets mounted and unmounted is not reliably announced, so the wrapper
// persists and only its text changes.

const Line = styled.p`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  min-height: 18px;
  font-size: 12px;
  line-height: 1.5;
  color: ${(props) => (props.$emphasis ? brand_orange_deep : "#6b7280")};
  font-weight: ${(props) => (props.$emphasis ? 700 : 500)};

  [data-theme="dark"] & {
    color: ${(props) => (props.$emphasis ? "#fbbf24" : "#9ca3af")};
  }
`;

// Three dots that fade in sequence. Communicates "still counting down" without a
// spinner, which would read as "request in flight" — it is not one yet.
const Dots = styled.span`
  display: inline-flex;
  gap: 3px;

  span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: currentColor;
    animation: search-status-pulse 1200ms ease-in-out infinite;
  }
  span:nth-child(2) {
    animation-delay: 160ms;
  }
  span:nth-child(3) {
    animation-delay: 320ms;
  }

  @keyframes search-status-pulse {
    0%,
    100% {
      opacity: 0.25;
    }
    50% {
      opacity: 1;
    }
  }

  /* Respect a reduced-motion preference — hold the dots steady instead. */
  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
      opacity: 0.6;
    }
  }
`;

// Its own memoized component so the three <span>s are not re-created on each
// keystroke — the phase text changes far more often than the dots do.
const PulsingDots = React.memo(() => (
  <Dots aria-hidden="true">
    <span />
    <span />
    <span />
  </Dots>
));
PulsingDots.displayName = "PulsingDots";

function SearchStatus({
  phase,
  charactersNeeded = 0,
  minLength = 3,
  loading = false,
  id,
}) {
  let content = null;
  let emphasis = false;

  // `loading` wins over the debounce phases: once the request is out, that is the
  // most useful thing to report.
  if (loading) {
    content = (
      <>
        <PulsingDots />
        Mengambil hasil…
      </>
    );
  } else if (phase === SEARCH_PHASE.tooShort) {
    emphasis = true;
    content = `Ketik minimal ${minLength} karakter untuk mencari (${charactersNeeded} lagi).`;
  } else if (phase === SEARCH_PHASE.waiting) {
    content = (
      <>
        <PulsingDots />
        Menunggu kamu selesai mengetik… tekan Enter untuk mencari sekarang.
      </>
    );
  }

  return (
    <Line id={id} role="status" aria-live="polite" $emphasis={emphasis}>
      {content}
    </Line>
  );
}

// Memoized: re-renders only when the phase or `loading` actually changes, not on
// every keystroke that leaves the phase the same (typing the 5th through 14th
// character all sit in 'waiting').
export default React.memo(SearchStatus);
